/**
 * The one move the campaign home exists for: pick a campaign, choose where the
 * audio comes from, optionally say what you want out of tonight, and start.
 *
 * Its own component so the tutorial film renders this exact card rather than
 * a lookalike that drifts from the real one.
 */
import type { Campaign } from '@/api/types';
import type { AudioSource } from '@/audio/MicrophoneSession';
import { AudioSourcePicker } from '@/components/AudioSourcePicker';
import { Select } from '@/components/Select';

interface Props {
  campaigns: Campaign[];
  selectedId: string;
  onSelect: (id: string) => void;
  audioSource: AudioSource;
  onAudioSource: (source: AudioSource) => void;
  intent: string;
  onIntent: (intent: string) => void;
  error: string | null;
  busy: boolean;
  onStart: () => void;
  onNewCampaign: () => void;
}

export function StartSession({
  campaigns,
  selectedId,
  onSelect,
  audioSource,
  onAudioSource,
  intent,
  onIntent,
  error,
  busy,
  onStart,
  onNewCampaign,
}: Props) {
  const selected = campaigns.find((c) => c.id === selectedId) ?? null;

  return (
    <section className="card start-card">
      <header className="card__head">
        <div>
          <p className="eyebrow">When the table is ready</p>
          <h2 className="card__title">Start a session</h2>
        </div>
      </header>
      <Select
        label="Campaign"
        value={selectedId}
        onChange={onSelect}
        options={campaigns.map((c) => ({
          value: c.id,
          label: c.name,
          // A native option could only ever be a name. With three campaigns
          // of similar names this is the line that tells them apart.
          hint: firstLines(c.premise),
        }))}
      />
      {selected ? (
        <p className="card__body start-card__premise">
          {firstLines(selected.premise)}
        </p>
      ) : null}
      <AudioSourcePicker value={audioSource} onChange={onAudioSource} />
      <label className="field">
        <span className="field__label">
          What do you want out of tonight? (optional)
        </span>
        <textarea
          className="field__input field__input--multiline"
          rows={3}
          value={intent}
          placeholder="I'd like them to reach the harbour and meet Kestrel. Marek's brother subplot is overdue."
          onChange={(e) => onIntent(e.target.value)}
        />
        <span className="field__hint">
          Give the story a direction. Throughline follows your players as their
          plans change. Only you see these notes.
        </span>
      </label>
      {error ? (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="start-card__actions">
        <button
          type="button"
          className="button button--primary"
          onClick={onStart}
          disabled={busy || !selectedId}
        >
          {busy ? (
            <>
              <span className="spinner" aria-hidden="true" />
              Starting…
            </>
          ) : (
            <>
              Start session
              <ArrowIcon />
            </>
          )}
        </button>
        <button
          type="button"
          className="button button--quiet"
          onClick={onNewCampaign}
        >
          New campaign
        </button>
      </div>
    </section>
  );
}

/** A forward arrow for the one action on a screen that moves the game on. */
function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

function firstLines(premise: string, max = 220): string {
  const body = premise
    .split('\n')
    .find((l) => l.startsWith('- World, setting, and premise:'))
    ?.replace('- World, setting, and premise:', '')
    .trim();
  const text = body || premise;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
